<template>
  <div :class="cardClasses">
    <!-- Header -->
    <div
      v-if="$slots.header || title"
      :class="headerClasses"
    >
      <slot name="header">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ title }}
          </h3>
          <slot name="actions" />
        </div>
      </slot>
    </div>

    <!-- Body -->
    <div :class="bodyClasses">
      <slot />
    </div>

    <!-- Footer -->
    <div
      v-if="$slots.footer"
      :class="footerClasses"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'bordered', 'elevated', 'flat'].includes(value)
  },
  padding: {
    type: String,
    default: 'normal',
    validator: (value) => ['none', 'compact', 'normal', 'comfortable'].includes(value)
  },
  noDivider: {
    type: Boolean,
    default: false
  },
  hoverable: {
    type: Boolean,
    default: false
  }
})

const cardClasses = computed(() => {
  const classes = ['rounded-lg']

  // Variant styles
  const variants = {
    default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
    bordered: 'bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600',
    elevated: 'bg-white dark:bg-gray-800 shadow-md',
    flat: 'bg-gray-50 dark:bg-gray-900'
  }
  classes.push(variants[props.variant])

  // Hoverable effect
  if (props.hoverable) {
    classes.push('transition-shadow duration-200 hover:shadow-lg')
  }

  return classes.join(' ')
})

const headerClasses = computed(() => {
  const classes = []

  // Padding
  const paddings = {
    none: '',
    compact: 'px-3 py-2',
    normal: 'px-4 py-3',
    comfortable: 'px-6 py-4'
  }
  classes.push(paddings[props.padding])

  // Border
  if (!props.noDivider) {
    classes.push('border-b border-gray-200 dark:border-gray-700')
  }

  return classes.join(' ')
})

const bodyClasses = computed(() => {
  const paddings = {
    none: '',
    compact: 'p-3',
    normal: 'p-4',
    comfortable: 'p-6'
  }
  return paddings[props.padding]
})

const footerClasses = computed(() => {
  const classes = []

  // Padding
  const paddings = {
    none: '',
    compact: 'px-3 py-2',
    normal: 'px-4 py-3',
    comfortable: 'px-6 py-4'
  }
  classes.push(paddings[props.padding])

  // Border
  if (!props.noDivider) {
    classes.push('border-t border-gray-200 dark:border-gray-700')
  }

  return classes.join(' ')
})
</script>
